import React, { useState, useEffect } from 'react';
import { Container, Badge, Spinner, Modal, Button } from 'react-bootstrap';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { DataGrid } from 'react-data-grid';

function AdminDashboard({ user }) {
    const [interviews, setInterviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedTranscript, setSelectedTranscript] = useState(null);
    const [selectedSummary, setSelectedSummary] = useState(null);
    const navigate = useNavigate();
    const SERVER_URL = process.env.REACT_APP_SERVER_URL;

    // Hide body scrollbar so only DataGrid's internal scrollbar shows
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    useEffect(() => {
        if (!user?.is_admin) {
            navigate('/');
            return;
        }

        const fetchInterviews = async () => {
            try {
                const response = await axios.get(`${SERVER_URL}/admin/interviews`, {
                    withCredentials: true
                });
                if (response.data.status === 'success') {
                    // Add a unique ID for the datagrid if not present
                    const formattedData = response.data.interviews.map((item, index) => ({
                        ...item,
                        sr_no: index + 1
                    }));
                    setInterviews(formattedData);
                } else {
                    setError('Failed to load interviews.');
                }
            } catch (err) {
                console.error(err);
                setError('Error fetching data.');
            } finally {
                setLoading(false);
            }
        };

        fetchInterviews();
    }, [user, navigate, SERVER_URL]);

    const handleShowTranscript = (transcript) => setSelectedTranscript(transcript);
    const handleCloseTranscript = () => setSelectedTranscript(null);

    const handleShowSummary = (summary) => setSelectedSummary(summary);
    const handleCloseSummary = () => setSelectedSummary(null);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const columns = [
        { key: 'sr_no', name: 'Sr.', width: 60, resizable: true },
        { key: 'candidate_name', name: 'Candidate Name', width: 200, resizable: true },
        {
            key: 'job_role', name: 'Job Role', width: 150, resizable: true,
            renderCell: ({ row }) => <Badge bg='secondary' className="text-light fw-medium rounded-pill px-3 py-2">{row.job_role}</Badge>
        },
        {
            key: 'resume_matching_score', name: 'Resume Match Score', width: 150, resizable: true,
            // renderCell: ({ row }) => <Badge bg='secondary' className="text-light fw-medium rounded-pill px-3 py-2">{row.resume_matching_score}</Badge>
        },
        {
            key: 'resume_matching_summary', name: 'Resume Match Summary', width: 150, resizable: true
        },
        { key: 'start_time', name: 'Start Time', width: 180, resizable: true, renderCell: ({ row }) => formatDate(row.start_time) },
        { key: 'end_time', name: 'End Time', width: 180, resizable: true, renderCell: ({ row }) => formatDate(row.end_time) },
        {
            key: 'recording_url', name: 'Recording', width: 260, resizable: true,
            renderCell: ({ row }) => {
                if (!row.recording_url) return <span className="text-muted fst-italic small">N/A</span>;
                return <audio controls src={row.recording_url} style={{ height: '40px', width: '230px', marginTop: '5px' }} />;
            }
        },
        {
            key: 'summary', name: 'Result Summary', width: 180,
            renderCell: ({ row }) => (
                <Button
                    variant="primary"
                    size="sm"
                    className="rounded-pill shadow-sm fw-medium w-100"
                    style={{ backgroundColor: 'var(--primary-color, #003263)', borderColor: 'var(--primary-color, #003263)', marginTop: '8px' }}
                    onClick={() => handleShowSummary(row.summary)}
                    disabled={!row.summary}
                >
                    View Summary
                </Button>
            )
        },
        {
            key: 'transcript', name: 'Transcript', width: 180,
            renderCell: ({ row }) => (
                <Button
                    variant="outline-secondary"
                    size="sm"
                    className="rounded-pill shadow-sm fw-medium w-100"
                    style={{ color: 'var(--primary-color, #003263)', borderColor: 'var(--primary-color, #003263)', marginTop: '8px' }}
                    onClick={() => handleShowTranscript(row.transcript)}
                    disabled={!row.transcript}
                >
                    View Transcript
                </Button>
            )
        }
    ];

    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center vh-100">
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-5 pt-5 text-center">
                <h4 className="text-danger">{error}</h4>
            </Container>
        );
    }

    return (
        <Container fluid className="p-0 mt-5 d-flex flex-column" style={{ height: 'calc(100vh - 3.5rem)', backgroundColor: 'var(--background-color, #f4f7f6)' }}>
            <DataGrid
                columns={columns}
                rows={interviews}
                className="rdg-light flex-grow-1"
                style={{ blockSize: '100%' }}
                rowHeight={55}
                rowKeyGetter={(row) => row.id}
            />

            {/* Transcript Modal */}
            <Modal show={selectedTranscript !== null} onHide={handleCloseTranscript} size="lg" centered>
                <Modal.Header closeButton className="border-bottom-0 pb-0">
                    <Modal.Title className="fw-bold text-custom" style={{ color: 'var(--primary-color, #003263)' }}>Interview Transcript</Modal.Title>
                </Modal.Header>
                <Modal.Body className="bg-light rounded m-3 p-4" style={{ maxHeight: '60vh', overflowY: 'auto', whiteSpace: 'pre-wrap', border: '1px solid #e9ecef', fontSize: '14px', lineHeight: '1.5' }}>
                    {selectedTranscript}
                </Modal.Body>
            </Modal>

            {/* Summary Modal */}
            <Modal show={selectedSummary !== null} onHide={handleCloseSummary} size="md" centered>
                <Modal.Header closeButton className="border-bottom-0 pb-0">
                    <Modal.Title className="fw-bold text-custom" style={{ color: 'var(--primary-color, #003263)' }}>Result Summary</Modal.Title>
                </Modal.Header>
                <Modal.Body className="bg-white rounded m-3 p-4 shadow-sm" style={{ maxHeight: '60vh', overflowY: 'auto', whiteSpace: 'pre-wrap', border: '1px solid #e9ecef', fontSize: '14px', lineHeight: '1.5' }}>
                    {selectedSummary || <span className="text-muted fst-italic">No summary available for this session.</span>}
                </Modal.Body>
            </Modal>

        </Container>
    );
}

export default AdminDashboard;
